'use client';

import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Table } from '@ravago/shared/radiance/components/table/components/Table/Table';
import { TableBody } from '@ravago/shared/radiance/components/table/components/TableBody/TableBody';
import { TableCell } from '@ravago/shared/radiance/components/table/components/TableCell/TableCell';
import { TableRow } from '@ravago/shared/radiance/components/table/components/TableRow/TableRow';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { CombinedQuoteQuantity } from '../../../utils/quote-pricing/combined-pricing.utils';
import { capitalizeFirstLetter } from '../../../utils/string-utils';

interface Props {
   quoteQuantities: CombinedQuoteQuantity[];
   selectedLineId: string | undefined;
   selectedQuantity: number | undefined;
   selectedLinePackaging: string | undefined;
   isQuantityValid: boolean;
   isSelectable?: boolean;
   leadTimeByLineId?: Record<string, boolean>;
   onSelect: (quantity: number, quoteLineId: string) => void;
}

const getPackagingUnitLabel = (
   quantity: number,
   line: CombinedQuoteQuantity['line'],
   formatter: ReturnType<typeof useFormatter>,
   translate: ReturnType<typeof useTranslations>
) => {
   const palletAmount = line.linePalletSizeAmount;
   const packagingIncrement = line.lineNetWeightAmount;

   if (palletAmount && palletAmount > 0 && quantity >= palletAmount) {
      return `${translate('dynamic-product-block.pallet-label')} ${formatter.quantity({
         amount: palletAmount,
         unitOfMeasure: line.lineQuantityUOM
      })} / ${translate('dynamic-product-block.unit')}`;
   }

   if (packagingIncrement && packagingIncrement > 0) {
      return `${formatter.quantity({ amount: packagingIncrement, unitOfMeasure: line.lineQuantityUOM })} / ${translate(
         'dynamic-product-block.unit'
      )}`;
   }

   return undefined;
};

export const QuoteLinesList = ({
   quoteQuantities,
   selectedLineId,
   selectedQuantity,
   selectedLinePackaging,
   isQuantityValid,
   isSelectable = true,
   leadTimeByLineId = {},
   onSelect
}: Props) => {
   const formatter = useFormatter();
   const translate = useTranslations();
   const showLeadTimeIndicator = Object.values(leadTimeByLineId).some(Boolean);

   return (
      <Table rowDividers>
         <TableBody>
            {quoteQuantities.map(({ quantity, quantityTo, line }) => {
               const packagingUnitLabel = getPackagingUnitLabel(quantity, line, formatter, translate);

               return (
                  <TableRow
                     key={`${line.lineId}-${line.linePackaging}-${quantity}`}
                     active={
                        isQuantityValid &&
                        (selectedLineId === line.lineId || (selectedQuantity === quantity && selectedLinePackaging === line.linePackaging))
                     }
                     onClick={isSelectable ? () => onSelect(quantity, line.lineId) : undefined}
                  >
                     <TableCell>
                        <Box direction="column" gap="3xs">
                           <Typography variant="small">
                              {quantityTo && quantityTo > quantity
                                 ? `${formatter.quantity({ amount: quantity, unitOfMeasure: line.lineQuantityUOM })} - ${formatter.quantity({
                                      amount: quantityTo,
                                      unitOfMeasure: line.lineQuantityUOM
                                   })}`
                                 : formatter.quantity({ amount: quantity, unitOfMeasure: line.lineQuantityUOM })}
                           </Typography>
                        </Box>
                     </TableCell>
                     <TableCell>
                        <Box direction="column" gap="3xs">
                           <Typography variant="small">{capitalizeFirstLetter(line.linePackaging)}</Typography>
                           {packagingUnitLabel && (
                              <Typography variant="small" color="subtle">
                                 {capitalizeFirstLetter(packagingUnitLabel)}
                              </Typography>
                           )}
                        </Box>
                     </TableCell>
                     <TableCell align="right">
                        <Typography variant="small-highlight">{formatter.unitPrice(line.lineUnitPrice)}</Typography>
                        {showLeadTimeIndicator && (
                           <Box direction="row" items="center" justify="end" gap="2xs">
                              <Icon icon="truck" size="small" color="subtle" />
                              <Typography variant="small" color="subtle">
                                 {translate(
                                    leadTimeByLineId[line.lineId]
                                       ? 'request-for-quotation.lead-time.mts-local-days'
                                       : 'request-for-quotation.lead-time.delivery-on-request'
                                 )}
                              </Typography>
                           </Box>
                        )}
                     </TableCell>
                  </TableRow>
               );
            })}
         </TableBody>
      </Table>
   );
};
